import { useState } from "react";
import { X, Check, ChevronLeft, Loader2, Hand, Users, UserPlus, Sunrise, Sun, Moon, Clock } from "lucide-react";
import { localISO, weekdayDayLabel } from "@/lib/labels";

const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d; };
const SLOTS = [
  { v: "ochtend", l: "Ochtend", I: Sunrise },
  { v: "middag", l: "Overdag", I: Sun },
  { v: "avond", l: "Vanavond", I: Moon },
  { v: "specifiek", l: "Exacte tijd", I: Clock },
];

const Opt = ({ on, onClick, children, testid, disabled }) => (
  <button data-testid={testid} disabled={disabled} onClick={onClick}
    className="rounded-2xl px-4 py-3.5 font-head font-bold text-lg text-start border-2 flex items-center gap-3 disabled:opacity-50 active:scale-[0.98] transition-transform"
    style={{ borderColor: "var(--k-accent)", background: on ? "var(--k-accent)" : "transparent", color: on ? "var(--k-accent-ink)" : "var(--k-text)" }}>
    {children}
  </button>
);

export const QuickTileWizard = ({ title, users, busy, onSubmit, onClose, kt, loc, style }) => {
  const [step, setStep] = useState(0);
  const [offset, setOffset] = useState(0);
  const [timed, setTimed] = useState(null);
  const [slot, setSlot] = useState(null);
  const [time, setTime] = useState("18:00");
  const [picking, setPicking] = useState(false);
  const [sel, setSel] = useState([]);

  const dayName = (o) => (o === 0 ? kt("Vandaag") : o === 1 ? kt("Morgen") : weekdayDayLabel(localISO(addDays(o)), loc));
  const when = () => {
    const base = { due_date: localISO(addDays(offset)) };
    if (!timed || !slot) return { ...base, due_when: "vandaag" };
    if (slot === "specifiek") return { ...base, due_when: "specifiek", due_time: time };
    return { ...base, due_when: slot };
  };
  const summary = () => {
    const s = SLOTS.find((x) => x.v === slot);
    return `${dayName(offset)}${timed && s ? ` · ${slot === "specifiek" ? time : kt(s.l)}` : ""}`;
  };
  const submit = (who, label) => onSubmit({ ...when(), ...who }, `${label} · ${summary()}`);
  const back = () => { if (picking) { setPicking(false); return; } setStep((s) => Math.max(0, s - 1)); };
  const timeOk = timed === false || (timed && slot && (slot !== "specifiek" || time));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.6)" }} data-testid="kiosk-qt-wizard">
      <div className="k-card w-full max-w-md p-6" style={style}>
        <div className="flex items-center justify-between mb-1 gap-2">
          <h3 className="font-head font-black text-2xl truncate">{title || kt("Taak inplannen")}</h3>
          <button data-testid="kiosk-qt-close" disabled={busy} onClick={onClose} className="p-2 rounded-full disabled:opacity-40 shrink-0" style={{ background: "var(--k-accent)", color: "var(--k-accent-ink)" }}><X className="w-5 h-5" strokeWidth={3} /></button>
        </div>
        <div className="flex gap-1.5 mb-5" data-testid="kiosk-qt-progress">
          {[0, 1, 2].map((i) => <span key={i} className="h-1.5 flex-1 rounded-full" style={{ background: i <= step ? "var(--k-accent)" : "rgba(0,0,0,0.12)" }} />)}
        </div>

        {step === 0 && (
          <div data-testid="kiosk-qt-step-when">
            <p className="font-head font-black text-lg mb-3">{kt("Wanneer?")}</p>
            <div className="grid grid-cols-2 gap-2">
              {[0, 1, 2, 3, 4, 5, 6].map((o) => (
                <Opt key={o} testid={`kiosk-qt-day-${o}`} on={offset === o} onClick={() => { setOffset(o); setStep(1); }}>{dayName(o)}</Opt>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="kiosk-qt-step-time">
            <p className="font-head font-black text-lg mb-3">{kt("Specifieke tijd?")}</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <Opt testid="kiosk-qt-timed-no" on={timed === false} onClick={() => { setTimed(false); setSlot(null); setStep(2); }}>{kt("Nee")}</Opt>
              <Opt testid="kiosk-qt-timed-yes" on={timed === true} onClick={() => setTimed(true)}>{kt("Ja")}</Opt>
            </div>
            {timed && (
              <div className="grid grid-cols-2 gap-2" data-testid="kiosk-qt-slots">
                {SLOTS.map(({ v, l, I }) => (
                  <Opt key={v} testid={`kiosk-qt-slot-${v}`} on={slot === v} onClick={() => { setSlot(v); if (v !== "specifiek") setStep(2); }}>
                    <I className="w-5 h-5 shrink-0" strokeWidth={2.5} />{kt(l)}
                  </Opt>
                ))}
              </div>
            )}
            {timed && slot === "specifiek" && (
              <div className="mt-4 flex items-center gap-3">
                <input data-testid="kiosk-qt-time" type="time" value={time} onChange={(e) => setTime(e.target.value)}
                  className="rounded-2xl px-4 py-3 text-xl font-head font-black border-2 outline-none" style={{ borderColor: "var(--k-accent)", background: "var(--k-panel)", color: "var(--k-text)" }} />
                <button data-testid="kiosk-qt-time-next" disabled={!timeOk} onClick={() => setStep(2)} className="rounded-full px-5 py-3 font-head font-black disabled:opacity-40" style={{ background: "var(--k-accent)", color: "var(--k-accent-ink)" }}>{kt("Volgende")}</button>
              </div>
            )}
          </div>
        )}

        {step === 2 && !picking && (
          <div data-testid="kiosk-qt-step-who">
            <p className="font-head font-black text-lg mb-1">{kt("Wie?")}</p>
            <p className="text-sm font-semibold opacity-70 mb-3" data-testid="kiosk-qt-summary">{summary()}</p>
            <div className="grid grid-cols-1 gap-2">
              <Opt testid="kiosk-qt-who-open" disabled={busy} onClick={() => submit({ free_pickup: true }, kt("Open om op te pakken"))}>
                {busy ? <Loader2 className="w-6 h-6 animate-spin shrink-0" /> : <Hand className="w-6 h-6 shrink-0" strokeWidth={2.5} />}
                <span>{kt("Oppakken")}<span className="block text-sm font-semibold opacity-70">{kt("open laten, iedereen kan hem pakken")}</span></span>
              </Opt>
              <Opt testid="kiosk-qt-who-assign" disabled={busy} onClick={() => setPicking(true)}>
                <UserPlus className="w-6 h-6 shrink-0" strokeWidth={2.5} />
                <span>{kt("Toewijzen")}<span className="block text-sm font-semibold opacity-70">{kt("kies één of meer personen")}</span></span>
              </Opt>
              <Opt testid="kiosk-qt-who-each" disabled={busy || !users.length} onClick={() => submit({ assignee_ids: users.map((u) => u.user_id), assign_mode: "per_person" }, kt("Ieder een"))}>
                <Users className="w-6 h-6 shrink-0" strokeWidth={2.5} />
                <span>{kt("Ieder een")}<span className="block text-sm font-semibold opacity-70">{users.length} {kt("personen krijgen elk een eigen taak")}</span></span>
              </Opt>
            </div>
          </div>
        )}

        {step === 2 && picking && (
          <div data-testid="kiosk-qt-step-pick">
            <p className="font-head font-black text-lg mb-3">{kt("Aan wie?")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4 max-h-[40vh] overflow-y-auto">
              {users.map((m) => {
                const on = sel.includes(m.user_id);
                return (
                  <Opt key={m.user_id} testid={`kiosk-qt-person-${m.user_id}`} on={on} disabled={busy}
                    onClick={() => setSel((s) => (on ? s.filter((x) => x !== m.user_id) : [...s, m.user_id]))}>
                    {on && <Check className="w-5 h-5 shrink-0" strokeWidth={3} />}
                    <span className="truncate text-base">{m.name}</span>
                  </Opt>
                );
              })}
            </div>
            <button data-testid="kiosk-qt-assign-confirm" disabled={!sel.length || busy}
              onClick={() => submit({ assignee_ids: sel, assign_mode: sel.length > 1 ? "per_person" : null }, kt("Toegewezen"))}
              className="w-full rounded-2xl p-4 font-head font-black text-lg disabled:opacity-40 flex items-center justify-center gap-2"
              style={{ background: "var(--k-text)", color: "var(--k-panel)" }}>
              {busy && <Loader2 className="w-6 h-6 animate-spin" strokeWidth={3} />}
              {sel.length > 1 ? `${kt("Toewijzen aan")} ${sel.length} ${kt("personen")}` : kt("Toewijzen")}
            </button>
          </div>
        )}

        {(step > 0 || picking) && (
          <button data-testid="kiosk-qt-back" disabled={busy} onClick={back}
            className="mt-5 rounded-full px-5 py-2.5 font-head font-bold border-2 flex items-center gap-1 disabled:opacity-40" style={{ borderColor: "var(--k-accent)", color: "var(--k-text)" }}>
            <ChevronLeft className="w-4 h-4" strokeWidth={3} />{kt("Vorige")}
          </button>
        )}
      </div>
    </div>
  );
};
